import * as React from 'react';
import { useSelector } from 'react-redux';

import { Counter, NewbuildingTiledCarousel } from '../../components/NewbuildingTiledCarousel';
import { EMediaType, FullscreenGallery } from '../../components/NewbuildingTiledCarousel/FullscreenGallery';
import MORTGAGE_THUMB_SRC from '../../components/offer_gallery/createMortgageFrame/img/thumb.svg';
import { useIsMortgageBannerAvailable } from '../../hooks/mortgage/useIsMortgageBannerAvailable';
import { getCountSelector, isNewbuildingTiledCarouselEnabled } from '../../selectors/newObject';
import { getNewbuildingTiledCarouselFirstThreePhotos } from '../../selectors/newObject/getNewbuildingTiledCarouselFirstThreePhotos';
import { getNewbuildingTiledCarouselPhotos } from '../../selectors/newObject/getNewbuildingTiledCarouselPhotos';
import { selectIsBookedFromDeveloper } from '../../selectors/newbuildingBookedFromDeveloper';
import { TTabsType } from '../../types/newbuildingTiledCarousel';
import { MortgagedPropertyLabelsContainer } from '../MortgagedPropertyLabelsContainer';
import { NewbuildingPromoLabelContainer } from '../NewbuildingPromoLabel/NewbuildingPromoLabel';
import { HeaderContainer } from './Header/Header';
import { Tabs } from './Tabs/Tabs';
import { TourButtonContainer } from './TourButton/TourButton';
import { TourPopupContainer } from './TourPopupContainer/TourPopupContainer';
import { trackFullscreenOpen, trackFullscreenScrollDepth, trackFullscreenTabClick } from './tracking';

interface INewbuildingTiledCarouselContainer {
  openTourInPopUp?: boolean;
}

// Покрыты все основные сценарии
/* istanbul ignore next */
export const NewbuildingTiledCarouselContainer = ({ openTourInPopUp }: INewbuildingTiledCarouselContainer) => {
  const isEnabled = useSelector(isNewbuildingTiledCarouselEnabled);
  const images = useSelector(getNewbuildingTiledCarouselFirstThreePhotos);
  const allImages = useSelector(getNewbuildingTiledCarouselPhotos);
  const counter = useSelector(getCountSelector);
  const isBookedFromDeveloper = useSelector(selectIsBookedFromDeveloper);
  const isMortgageBannerAvailable = useIsMortgageBannerAvailable();

  const [isOpen, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [maxIndex, setMaxIndex] = React.useState(0);

  const onSlideClick = React.useCallback((index: number) => {
    setOpen(true);
    trackFullscreenOpen();
    setActiveIndex(index);
  }, []);

  const onSlideChange = React.useCallback(
    ({ currentSlide }: { currentSlide: number }) => {
      setActiveIndex(currentSlide);
      if (maxIndex < currentSlide) {
        setMaxIndex(currentSlide);
        trackFullscreenScrollDepth(currentSlide);
      }
    },
    [maxIndex],
  );

  const onTabClick = React.useCallback((currentSlide: number, type: TTabsType) => {
    setActiveIndex(currentSlide);
    trackFullscreenTabClick(type);
  }, []);

  const close = React.useCallback(() => setOpen(false), []);

  const allImagesWithMortgage = React.useMemo(() => {
    return isMortgageBannerAvailable
      ? allImages.concat({
          mediaType: EMediaType.Mortgage,
          thumbnailUrl: MORTGAGE_THUMB_SRC,
          alt: '',
          fullUrl: '',
          id: -1,
        })
      : allImages;
  }, [allImages, isMortgageBannerAvailable]);

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <NewbuildingTiledCarousel
        images={images}
        counter={<Counter counter={counter} />}
        promo={
          <>
            <NewbuildingPromoLabelContainer />
            <MortgagedPropertyLabelsContainer />
          </>
        }
        onClick={onSlideClick}
        tour={openTourInPopUp ? <TourPopupContainer /> : <TourButtonContainer />}
        isBooked={isBookedFromDeveloper}
      />
      {isOpen && (
        <FullscreenGallery
          header={<HeaderContainer tabs={<Tabs activeTab={activeIndex} onChange={onTabClick} />} />}
          defaultSlideIndex={activeIndex}
          title="test"
          onClose={close}
          onSlideChange={onSlideChange}
          images={allImagesWithMortgage}
        />
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default NewbuildingTiledCarouselContainer;
